import { Injectable } from "@angular/core";
import {
  forkJoin as observableForkJoin,
  Observable,
  of as observableOf,
} from "rxjs";
import { mergeMap, tap } from "rxjs/operators";
import { StatusApi } from "../api-services/status-api/status-api.models";
import { Status } from "../models/status";

@Injectable()
export class StatusService {
  // type de DDD: E, code statut: P (créé)
  // type de DDD: I, code statut: P (créé)
  // type de DDD: EI, code statut: A(accepté)
  // type de DDD: EI, code statut: E(ré-accepté)
  // type de DDD: EI, code statut: V(travaux en cours)
  // type de DDD: EI, code statut: F(travaux à planifier)
  // type de DDD: EI, code statut: L(travaux planifiés)
  // type de DDD: EI, code statut: N(travaux suspendus)
  // type de DDD: EI, code statut: S(transféré)
  // type de DDD: EI, code statut: T(terminé)
  // type de DDD: EI, code statut: X(refusé)

  // add photo is not ready to develop, it will be disabled for all status
  private statusChangeRules = [
    {
      status: "P",
      type: "E",
      possibleStatuses: ["A", "T", "X"],
      disabledSessions: [],
      validations: { reason: false, internalComment: false },
    },
    {
      status: "P",
      type: "I",
      possibleStatuses: ["A", "T", "X"],
      disabledSessions: ["returnDDS"],
      validations: { reason: false, internalComment: false },
    },
    {
      status: "A",
      type: "EI",
      possibleStatuses: ["T", "X"],
      disabledSessions: ["returnDDS"],
      validations: { reason: true, internalComment: true },
    },
    {
      status: "E",
      type: "EI",
      possibleStatuses: ["A", "T", "X"],
      disabledSessions: ["returnDDS"],
      validations: { reason: false, internalComment: false },
    },
    {
      status: "V",
      type: "EI",
      possibleStatuses: ["T", "X"],
      disabledSessions: ["returnDDS"],
      validations: { reason: false, internalComment: false },
    },
    {
      status: "F",
      type: "EI",
      possibleStatuses: ["A", "T", "X"],
      disabledSessions: ["returnDDS"],
      validations: { reason: false, internalComment: false },
    },
    {
      status: "L",
      type: "EI",
      possibleStatuses: ["A", "T", "X"],
      disabledSessions: ["returnDDS"],
      validations: { reason: false, internalComment: false },
    },
    {
      status: "N",
      type: "EI",
      possibleStatuses: ["A", "T", "X"],
      disabledSessions: ["returnDDS"],
      validations: { reason: false, internalComment: false },
    },
    {
      status: "S",
      type: "EI",
      possibleStatuses: ["A", "T", "X"],
      disabledSessions: ["returnDDS"],
      validations: { reason: false, internalComment: false },
    },
    {
      status: "T",
      type: "EI",
      possibleStatuses: [],
      disabledSessions: ["returnDDS", "updateActivity"],
      validations: { reason: true, internalComment: false },
    },
    {
      status: "X",
      type: "EI",
      possibleStatuses: [],
      disabledSessions: ["returnDDS", "updateActivity"],
      validations: { reason: true, internalComment: false },
    },
  ];
  private statuses: Status[] = [];

  constructor(private statusApiService: StatusApi) {}

  public getPossibleStatuses(code: string): Observable<Status[]> {
    if (!code) {
      return observableOf([]);
    }

    const rule = this.statusChangeRules.find((rule1) => rule1.status === code);

    if (!rule) {
      return observableOf([]);
    }

    return observableForkJoin(
      rule.possibleStatuses.map((statusCode) =>
        this.getStatusByCode(statusCode),
      ),
    );
  }

  public getDisabledSessions(code: string, type: string): string[] {
    if (!code || !type) {
      return [];
    }

    const rule = this.statusChangeRules.find(
      (rule1) => rule1.status === code && rule1.type.includes(type),
    );
    return rule ? rule.disabledSessions : [];
  }

  public getValidations(code: string): {
    reason: boolean;
    internalComment: boolean;
  } {
    if (!code) {
      return { reason: false, internalComment: false };
    }

    const rule = this.statusChangeRules.find((rule1) => rule1.status === code);
    return rule ? rule.validations : { reason: false, internalComment: false };
  }

  private findStatusName(code: string): string {
    const status: Status = this.statuses.find((status) => status.code === code);
    return status ? status.code : "";
  }

  private findStatus(code: string): Status {
    const t = this.statuses.find((status) => status.code === code);
    return t;
  }

  public getPredefinedTextByCode(code: string): Observable<string> {
    if (this.statuses.length) {
      return observableOf(this.findStatusName(code));
    } else {
      return this.getAllStatuses().pipe(
        mergeMap((val) => observableOf(this.findStatusName(code))),
      );
    }
  }

  public getAlreadyFetchedPredefinedTextByCode(code: string): string {
    if (!this.statuses?.length) {
      throw Error(
        `Tentative d'extraction de statut prédéfini n'ayant pas été obtenu au préalable. Veuillez contacter le support informatique.`,
      );
    }
    return this.findStatusName(code);
  }

  public getStatusByCode(code: string): Observable<Status> {
    if (this.statuses.length) {
      return observableOf(this.findStatus(code));
    } else {
      return this.getAllStatuses().pipe(
        mergeMap((val) => observableOf(this.findStatus(code))),
      );
    }
  }

  public getAllStatuses(): Observable<Status[]> {
    return this.statuses.length
      ? observableOf(this.statuses)
      : this.statusApiService
          .getPredefinedStatuses()
          .pipe(tap((statuses) => (this.statuses = statuses)));
  }
}
